import Rails from "@rails/ujs";
import { Controller } from "@hotwired/stimulus"

const INITIAL_PAUSE_MS = 250
const PROGRESS_DURATION_MS = 2000
const COMPLETION_HOLD_MS = 350
const EXIT_DURATION_MS = 250

export default class extends Controller {
  static targets = ["input", "transition", "progress", "progressRing"]

  connect() {
    this.submitting = false
    this.boundPageShow = this.resetTransition.bind(this)
    this.boundBeforeRender = this.holdTurboRender.bind(this)
    window.addEventListener("pageshow", this.boundPageShow)
    document.addEventListener("turbo:before-render", this.boundBeforeRender)
    this.resetTransition()
  }

  disconnect() {
    window.removeEventListener("pageshow", this.boundPageShow)
    document.removeEventListener("turbo:before-render", this.boundBeforeRender)
    if (this.progressFrame) cancelAnimationFrame(this.progressFrame)
    if (this.completionTimer) clearTimeout(this.completionTimer)
    if (this.exitTimer) clearTimeout(this.exitTimer)
  }

  submit() {
    if (this.submitting) return

    this.submitting = true
    this.showTransition()

    // Give mobile Safari two paint opportunities at 0% before Turbo starts the
    // request. Turbo keeps the current page paintable while the response loads.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.submitForm())
    })
  }

  submitForm() {
    // Prefer the native form submit (works without rails-ujs and respects
    // HTML validation); fall back to rails-ujs for older browsers.
    if (typeof this.element.requestSubmit === "function") {
      this.element.requestSubmit();
    } else {
      Rails.fire(this.element, 'submit');
    }
  }

  showTransition() {
    if (!this.hasTransitionTarget) return

    this.element.setAttribute("aria-busy", "true")
    this.transitionTarget.classList.remove("hidden")
    this.transitionTarget.classList.remove("onboarding-preparing--leaving")
    this.transitionTarget.setAttribute("aria-hidden", "false")
    this.startedAt = performance.now() + INITIAL_PAUSE_MS
    this.setProgress(0)
    this.progressFrame = requestAnimationFrame((timestamp) => this.updateProgress(timestamp))
  }

  updateProgress(now) {
    if (!this.submitting || !this.hasProgressTarget || !this.hasProgressRingTarget) return

    const elapsed = Math.max(0, now - this.startedAt)
    const progress = Math.min(100, Math.floor((elapsed / PROGRESS_DURATION_MS) * 100))

    this.setProgress(progress)

    if (progress === 100) {
      this.progressFrame = null
      this.finishProgress()
      return
    }

    this.progressFrame = requestAnimationFrame((timestamp) => this.updateProgress(timestamp))
  }

  setProgress(progress) {
    this.progressTarget.textContent = progress.toString()
    this.progressRingTarget.style.setProperty("--onboarding-progress", `${progress}%`)
  }

  holdTurboRender(event) {
    if (!this.submitting || this.progressComplete) return

    event.preventDefault()
    this.pendingRender = () => event.detail.resume()
    this.beginExitIfReady()
  }

  finishProgress() {
    if (this.completing) return

    this.completing = true
    this.completionTimer = setTimeout(() => {
      this.completionTimer = null
      this.completionHoldComplete = true
      this.beginExitIfReady()
    }, COMPLETION_HOLD_MS)
  }

  beginExitIfReady() {
    if (!this.completionHoldComplete || !this.pendingRender || this.exiting) return

    this.exiting = true
    this.transitionTarget.classList.add("onboarding-preparing--leaving")
    this.exitTimer = setTimeout(() => {
      this.exitTimer = null
      this.progressComplete = true
      this.releaseTurboRender()
    }, EXIT_DURATION_MS)
  }

  releaseTurboRender() {
    if (!this.pendingRender) return

    const resume = this.pendingRender
    this.pendingRender = null
    // Paint the completed ring once before replacing the page.
    requestAnimationFrame(() => resume())
  }

  resetTransition() {
    this.submitting = false
    this.progressComplete = false
    this.completing = false
    this.completionHoldComplete = false
    this.exiting = false
    this.pendingRender = null
    if (this.progressFrame) cancelAnimationFrame(this.progressFrame)
    if (this.completionTimer) clearTimeout(this.completionTimer)
    if (this.exitTimer) clearTimeout(this.exitTimer)
    this.progressFrame = null
    this.completionTimer = null
    this.exitTimer = null
    if (!this.hasTransitionTarget) return

    this.element.removeAttribute("aria-busy")
    this.transitionTarget.classList.add("hidden")
    this.transitionTarget.classList.remove("onboarding-preparing--leaving")
    this.transitionTarget.setAttribute("aria-hidden", "true")
    if (this.hasProgressTarget) this.progressTarget.textContent = "0"
    if (this.hasProgressRingTarget) this.progressRingTarget.style.setProperty("--onboarding-progress", "0%")
  }
}
